import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  useReveal, IndustryHero, IndustryIntro, UseCasesGrid,
  IndustryStats, IndustryDeliverables, IndustryCTA, Footer,
} from '../shared/IndustryPageSections';

const COLOR = '#C0392B';

const USE_CASES = [
  { icon:'thermometer', title:'Neonatal Care Solutions', to:'/industries/healthcare/neonatal-care-solutions', desc:(
    <ul style={{ margin:0, paddingLeft:16, display:'flex', flexDirection:'column', gap:4 }}>
      <li>Smart Baby Warmers</li>
      <li>Infant Temperature Monitoring</li>
      <li>NICU Device Development</li>
    </ul>
  ) },
  { icon:'sun', title:'Phototherapy Treatment Systems', to:'/industries/healthcare/phototherapy-treatment-systems', desc:(
    <ul style={{ margin:0, paddingLeft:16, display:'flex', flexDirection:'column', gap:4 }}>
      <li>LED Phototherapy Machines</li>
      <li>Treatment Monitoring</li>
      <li>Device Performance Analytics</li>
    </ul>
  ) },
  { icon:'monitor', title:'Remote Patient Monitoring', to:'/industries/healthcare/remote-patient-monitoring', desc:(
    <ul style={{ margin:0, paddingLeft:16, display:'flex', flexDirection:'column', gap:4 }}>
      <li>Real-Time Vital Sign Monitoring</li>
      <li>Doctor & Caregiver Dashboards</li>
      <li>Home Healthcare Connectivity</li>
    </ul>
  ) },
  { icon:'activity', title:'Nutrition Assessment & Digital Health Platforms', to:'/industries/healthcare/nutrition-assessment-digital-health', desc:(
    <ul style={{ margin:0, paddingLeft:16, display:'flex', flexDirection:'column', gap:4 }}>
      <li>Nutrition Analysis Systems</li>
      <li>Body Composition Measurement</li>
      <li>Patient Nutrition Monitoring Dashboards</li>
    </ul>
  ) },
  { icon:'plug', title:'Connected Medical Devices (Medical IoT)', to:'/industries/healthcare/connected-medical-devices', desc:(
    <ul style={{ margin:0, paddingLeft:16, display:'flex', flexDirection:'column', gap:4 }}>
      <li>Secure medical device integration and interoperability</li>
      <li>Real-time asset tracking and utilization analytics</li>
      <li>FDA and HIPAA-compliant data pipelines</li>
    </ul>
  ) },
];

const STATS = [
  { val:'$14.9B', label:'Global Healthcare IoT Market by 2028' },
  { val:'30%', label:'Reduction in hospital readmissions with remote patient monitoring' },
  { val:'45%', label:'Faster emergency response with smart hospital alerts' },
];

const DELIVERABLES = [
  'Custom wearable device design (hardware + firmware)',
  'HIPAA-aware cloud architecture for patient data',
  'Real-time dashboard for doctors and ward staff',
  'SMS/app alerts for abnormal vitals',
  'Integration with existing hospital management systems',
  'Secure, encrypted data transmission',
  'Cold chain monitoring hardware + compliance reporting',
  'Mobile app for patients, caregivers, and clinical staff',
];

const INTRO_BODY = [
  "India's healthcare system is under immense pressure — rising patient volumes, stretched clinical staff, and infrastructure gaps that make timely intervention difficult. IoT bridges the gap: connected sensors, wearables, and smart hospital systems bring patient data out of silos and into the hands of clinicians in real time, enabling earlier intervention and better outcomes.",
  'At Zone-X, we build healthcare IoT solutions that are clinically reliable, data-secure, and designed for the realities of Indian hospitals. From remote patient monitoring to fully automated smart wards, our systems improve care quality while reducing operational costs — with regulatory awareness built in from day one.',
];

export default function HealthcarePage() {
  const [heroRef, heroVisible] = useReveal();
  return (
    <>
      <Helmet>
        <title>IoT Solutions for Healthcare Industry India | Smart Hospital & Remote Patient Monitoring — Zone-X</title>
        <meta name="description" content="Zone-X builds healthcare IoT systems in India — remote patient monitoring, smart hospital automation, wearable medical devices, cold chain monitoring, and HIPAA-aware cloud platforms." />
        <link rel="canonical" href="https://zone-x.dev/industries/healthcare" />
      </Helmet>

      <div ref={heroRef}>
        <IndustryHero
          tag="Industry / Healthcare"
          breadcrumb="Healthcare"
          title="Smarter Healthcare Starts with"
          titleAccent="Connected Devices"
          subtitle="From remote patient monitoring to smart hospital automation — Zone-X builds IoT solutions that save time, reduce errors, and improve patient outcomes."
          color={COLOR}
          visible={heroVisible}
          visual="/visuals/industries/Industry 1.png"
        />
      </div>

      <IndustryIntro heading="Why Healthcare Needs IoT — Right Now" body={INTRO_BODY} color={COLOR} />
      <UseCasesGrid cases={USE_CASES} color={COLOR} />
      <IndustryStats stats={STATS} color={COLOR} />
      <IndustryDeliverables items={DELIVERABLES} color={COLOR} />
      <IndustryCTA />
      <Footer />
    </>
  );
}
